import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { ArrowLeft, Mail, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

// Mobile Forgot Password — for unified_users from the mobile portal.
// Mirrors the desktop V2 flow visually in a mobile card layout.

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const MobileForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Please enter your email address');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/v2/auth/forgot-password`, {
        email: email.toLowerCase().trim(),
      });
      setSubmitted(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-start p-4 pt-8 safe-top">
      <Card className="w-full max-w-md" data-testid="mobile-forgot-password-card">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-2">
            <div className="w-14 h-14 rounded-xl overflow-hidden">
              <img src="/equipr-logo.png" alt="EQUIPR" className="w-full h-full object-cover" />
            </div>
          </div>
          <CardTitle className="text-xl">
            {submitted ? 'Check your email' : 'Forgot password?'}
          </CardTitle>
          <CardDescription>
            {submitted
              ? 'If an account exists with that email, we sent you a reset link.'
              : 'Enter your email and we\'ll send a reset link.'}
          </CardDescription>
        </CardHeader>

        <CardContent>
          {submitted ? (
            <div className="space-y-6 text-center">
              <div className="flex justify-center">
                <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center">
                  <CheckCircle className="h-8 w-8 text-green-500" />
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                Sent to <strong>{email}</strong>.<br />Link expires in 60 minutes.
              </p>
              <div className="flex flex-col gap-2">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setSubmitted(false)}
                  data-testid="mobile-try-again-button"
                >
                  Try a different email
                </Button>
                <Button
                  className="w-full"
                  onClick={() => navigate('/mobile/login')}
                  data-testid="mobile-back-to-login-button"
                >
                  Back to sign in
                </Button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide">Email</Label>
                <Input
                  data-testid="mobile-forgot-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  required
                  autoFocus
                  inputMode="email"
                  autoComplete="email"
                />
              </div>
              <Button
                type="submit"
                className="w-full"
                disabled={loading}
                data-testid="mobile-forgot-submit-button"
              >
                {loading ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending...</>
                ) : (
                  <><Mail className="h-4 w-4 mr-2" />Send reset link</>
                )}
              </Button>
              <div className="pt-2 text-center">
                <Link
                  to="/mobile/login"
                  className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to sign in
                </Link>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MobileForgotPassword;
